import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Destroyable } from '../../../utils/mixins/destroyable.mixin';
import { SimulationsService } from '../../../services/simulations/simulations.service';
import { LoadingService } from '../../../services/loading.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { HttpHeaders } from '@angular/common/http';
import { COMPONENT_ID_HEADER } from '../../../core/consts/api.const';
import {
  ExtendedSimulationResults,
  PcCycle,
  PcSimulationResults,
  RunSearchFilters,
  SimulationFilterResult,
  SimulationResponse,
} from '../../../core/models/simulation.model';
import { SimulatorType } from '../../../core/enums/simulator-type.enum';
import { RunFilterOptionsPopupComponent } from '../run-filter-options-popup/run-filter-options-popup.component';
import { debounceTime, filter, map, merge, tap } from 'rxjs';
import Highcharts from 'highcharts';
import { HighchartsChartModule } from 'highcharts-angular';
import {
  createBaseLinearChartOptions,
  createLineSeries,
  updateChartSeriesVisibilitySmooth,
} from '../../../utils/chart/chart-config.util';
import { DEFAULT_DEBOUNCE_TIME } from '../../../core/consts/consts';
import { sanitizeSearchString } from '../../../utils/sanitize.util';
import { RemoveExtensionPipe } from '../../pipes/remove-extension.pipe';

@Component({
  selector: 'app-select-capillary-pressure-popup',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatRadioModule,
    MatSelectModule,
    MatFormFieldModule,
    MatCheckboxModule,
    MatInputModule,
    MatExpansionModule,
    MatPaginatorModule,
    MatSortModule,
    HighchartsChartModule,
    RemoveExtensionPipe,
  ],
  templateUrl: './select-capillary-pressure-popup.component.html',
  styleUrl: './select-capillary-pressure-popup.component.scss',
})
export class SelectCapillaryPressurePopupComponent extends Destroyable(Object) implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  private readonly componentId: string = 'select-capillary-pressure-popup';
  readonly Highcharts: typeof Highcharts = Highcharts;
  readonly primaryDrainageColor: string = '#4F81BD';
  readonly firstImbibitionColor: string = '#C0504D';
  readonly secondaryDrainageColor: string = '#8064A2';

  readonly displayedColumns: string[] = [
    'select',
    'name',
    'fileName',
    'direction',
    'cycle',
    'interfactialTension',
    'contactAnglePrimaryDrainage',
    'contactAngleFirstImbibition',
  ];
  readonly searchByOptions: { label: string; value: string }[] = [
    { label: 'Name', value: 'Name' },
    { label: 'Rock file', value: 'File' },
  ];

  filterByOptionSelected: string = 'File';
  searchString: string = '';
  dataSource = new MatTableDataSource<ExtendedSimulationResults>([]);
  totalCount: number = 0;
  currentPage: number = 0;
  currentPageSize: number = 10;
  filterData!: RunSearchFilters;
  isLoadingTable: boolean = false;

  selectedRun: ExtendedSimulationResults | null = null;
  selectedSimulation: SimulationResponse<PcSimulationResults> | null = null;
  cyclesForm!: FormGroup;
  chart?: Highcharts.Chart;
  chartOptions!: Highcharts.Options;
  isChartCreated: boolean = false;

  private preselection: {
    simulationId: string;
    direction: string;
  } | null = null;

  constructor(
    public readonly dialogRef: MatDialogRef<SelectCapillaryPressurePopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      fileName: string;
      preselection?: { simulationId: string; direction: string };
    },
    private readonly simulationsService: SimulationsService,
    private readonly loadingService: LoadingService,
    private readonly snackbarService: SnackbarService,
    private readonly fb: FormBuilder,
    private readonly dialog: MatDialog,
  ) {
    super();
    this.preselection = data.preselection || null;
  }

  get resultForSelectedSimulation(): PcSimulationResults | undefined {
    return this.selectedSimulation?.results?.[0];
  }

  get swd1Value(): string[] {
    return this.resultForSelectedSimulation?.result?.swD1 ?? [];
  }

  get pcd1Value(): string[] {
    return this.resultForSelectedSimulation?.result?.pcD1 ?? [];
  }

  get swImbValue(): string[] {
    return this.resultForSelectedSimulation?.result?.swImb ?? [];
  }

  get pcImbValue(): string[] {
    return this.resultForSelectedSimulation?.result?.pcImb ?? [];
  }

  get swd2Value(): string[] {
    return this.resultForSelectedSimulation?.result?.swD2 ?? [];
  }

  get pcd2Value(): string[] {
    return this.resultForSelectedSimulation?.result?.pcD2 ?? [];
  }

  ngOnInit(): void {
    this.initCyclesForm();
    this.initFilterData();
    this.loadSimulations();
  }

  ngAfterViewInit(): void {
    if (this.paginator) {
      this.paginator.page
        .pipe(
          debounceTime(DEFAULT_DEBOUNCE_TIME),
          tap(() => {
            this.currentPage = this.paginator.pageIndex;
            this.currentPageSize = this.paginator.pageSize;
            this.loadSimulations();
          }),
          this.takeUntilDestroyed(),
        )
        .subscribe();
    }

    if (this.sort) {
      this.sort.sortChange
        .pipe(
          debounceTime(DEFAULT_DEBOUNCE_TIME),
          this.takeUntilDestroyed(),
        )
        .subscribe((sortState) => {
          if (sortState.direction) {
            this.filterData.sortColumnName = this.mapSortColumn(sortState.active);
            this.filterData.sortDirection = sortState.direction === 'asc' ? 'Asc' : 'Desc';
          } else {
            this.filterData.sortColumnName = null;
            this.filterData.sortDirection = null;
          }
          this.currentPage = 0;
          this.loadSimulations();
        });
    }
  }

  getCycleControl(cycle: PcCycle): FormControl {
    return this.cyclesForm.get(cycle) as FormControl;
  }

  isRunResultValid(result: ExtendedSimulationResults): boolean {
    return result.status?.toLowerCase() === 'success';
  }

  isCycleResultExists(cycle: PcCycle): boolean {
    if (!this.resultForSelectedSimulation?.result) return false;

    switch (cycle) {
      case 'd1':
        return Boolean(
          this.pcd1Value.length &&
          this.swd1Value.length &&
          this.selectedRun?.parameters?.simulatePrimaryDrainage,
        );
      case 'imb':
        return Boolean(
          this.pcImbValue.length &&
          this.swImbValue.length &&
          this.selectedRun?.parameters?.simulateFirstImbibition,
        );
      case 'd2':
        return false;
    }
  }

  getCyclesForRun(run: ExtendedSimulationResults): string {
    const cycles: string[] = [];
    if (run.parameters?.simulatePrimaryDrainage) cycles.push('PD');
    if (run.parameters?.simulateFirstImbibition) cycles.push('FI');
    if (run.parameters?.simulateSecondaryDrainage) cycles.push('SD');
    return cycles.join('/');
  }

  onPcSimulationSelectionChange(run: ExtendedSimulationResults): void {
    this.selectedRun = run;

    const options = {
      headers: new HttpHeaders({
        [COMPONENT_ID_HEADER]: this.componentId,
      }),
    };

    this.simulationsService
      .getSimulationById(run.simulationId, options)
      .pipe(this.takeUntilDestroyed())
      .subscribe((response: SimulationResponse<PcSimulationResults>) => {
        this.selectedSimulation = {
          ...response,
          results: response.results.filter(
            (result: PcSimulationResults) =>
              result.direction.toLowerCase() === run.direction.toLowerCase(),
          ),
        };

        this.configureCyclesFormControls();
        if (this.isChartCreated) {
          this.reinitializeChartSeries();
        } else {
          this.initChart();
          this.isChartCreated = true;
        }
      });
  }

  saveChartInstance(chart: Highcharts.Chart): void {
    this.chart = chart;
    this.initializeChartSeries();
  }

  onSearch(): void {
    this.currentPage = 0;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.loadSimulations();
  }

  onClearSearch(): void {
    this.searchString = '';
    this.onSearch();
  }

  onSearchStringChange(): void {
    if (!this.searchString) {
      this.onClearSearch();
    }
  }

  openAdvancedFiltersPopup(): void {
    const dialogRef = this.dialog.open(RunFilterOptionsPopupComponent, {
      disableClose: true,
      data: {
        ...this.filterData,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter(Boolean),
        this.takeUntilDestroyed(),
      )
      .subscribe((updatedFilters: RunSearchFilters) => {
        this.filterData = updatedFilters;
        this.onSearch();
      });
  }

  onConfirm(): void {
    if (!this.selectedRun || !this.selectedSimulation) return;

    const selectedCycles: string[] = [];
    if (this.getCycleControl('d1').value) selectedCycles.push('PD');
    if (this.getCycleControl('imb').value) selectedCycles.push('FI');
    if (this.getCycleControl('d2').value) selectedCycles.push('SD');

    this.dialogRef.close({
      pcSimulationId: this.selectedRun.simulationId,
      pcSimulationName: this.selectedRun.name,
      pcDirection: this.selectedRun.direction,
      pcCycles: selectedCycles,
      interfactialTension: this.selectedRun.parameters?.interfactialTension ?? 0,
      contactAnglePrimaryDrainage: this.selectedRun.parameters?.contactAnglePrimaryDrainage ?? 0,
      contactAngleFirstImbibition: this.selectedRun.parameters?.contactAngleFirstImbibition ?? 0,
    });
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  private initFilterData(): void {
    this.filterData = {
      filterBy: this.filterByOptionSelected,
      searchString: sanitizeSearchString(this.searchString),
      type: SimulatorType.SubsamplePC,
      direction: [],
      cycles: [],
      interfacialTensionMin: null,
      interfacialTensionMax: null,
      primaryDrainageMin: null,
      primaryDrainageMax: null,
      firstImbibitionMin: null,
      firstImbibitionMax: null,
      sortColumnName: null,
      sortDirection: null,
      page: 1,
      pageSize: this.currentPageSize,
    };
  }

  private loadSimulations(): void {
    this.isLoadingTable = true;

    this.filterData = {
      ...this.filterData,
      filterBy: this.filterByOptionSelected,
      searchString: sanitizeSearchString(this.searchString),
      page: this.currentPage + 1,
      pageSize: this.currentPageSize,
    };

    this.simulationsService
      .getFilteredRuns(this.filterData)
      .pipe(this.takeUntilDestroyed())
      .subscribe({
        next: (result: SimulationFilterResult<ExtendedSimulationResults>) => {
          this.dataSource.data = result.data;
          this.totalCount = result.count;
          this.isLoadingTable = false;
          this.applyPreselection();
        },
        error: () => {
          this.isLoadingTable = false;
          this.snackbarService.openCustom({
            message: 'Failed to load capillary pressure simulations',
            title: 'Error',
            type: 'error',
            action: 'Close',
          });
        },
      });
  }

  private applyPreselection(): void {
    if (!this.preselection) return;

    const match = this.dataSource.data.find(
      (run) =>
        run.simulationId === this.preselection!.simulationId &&
        run.direction?.toLowerCase() === this.preselection!.direction?.toLowerCase(),
    );
    if (!match) return;

    this.onPcSimulationSelectionChange(match);
    this.preselection = null;
  }

  private initCyclesForm(): void {
    this.cyclesForm = this.fb.group({
      d1: [{ value: false, disabled: true }],
      imb: [{ value: false, disabled: true }],
      d2: [{ value: false, disabled: true }],
    });

    merge(
      this.getCycleControl('d1').valueChanges.pipe(map((value) => ({ cycle: 'd1', value }))),
      this.getCycleControl('imb').valueChanges.pipe(map((value) => ({ cycle: 'imb', value }))),
      this.getCycleControl('d2').valueChanges.pipe(map((value) => ({ cycle: 'd2', value }))),
    )
      .pipe(this.takeUntilDestroyed())
      .subscribe(() => {
        this.updateChartSeriesVisibility();
      });
  }

  private configureCyclesFormControls(): void {
    if (this.selectedSimulation) {
      (['d1', 'imb', 'd2'] as PcCycle[]).forEach((cycle: PcCycle) => {
        const cycleResultExists: boolean = this.isCycleResultExists(cycle);

        if (cycleResultExists) {
          this.getCycleControl(cycle).patchValue(true, { emitEvent: false });
          this.getCycleControl(cycle).enable({ emitEvent: false });
        } else {
          this.getCycleControl(cycle).patchValue(false, { emitEvent: false });
          this.getCycleControl(cycle).disable({ emitEvent: false });
        }
      });
    } else {
      (['d1', 'imb', 'd2'] as PcCycle[]).forEach((cycle: PcCycle) => {
        this.getCycleControl(cycle).patchValue(false, { emitEvent: false });
        this.getCycleControl(cycle).disable({ emitEvent: false });
      });
    }
  }

  private initChart(): void {
    this.chartOptions = createBaseLinearChartOptions({
      title: 'Capillary Pressure (Pc)',
      xAxisTitle: 'Saturation (fraction)',
      yAxisTitle: 'Capillary Pressure (psi)',
      xMin: 0,
      xMax: 1,
      series: [],
      tooltipFormatter: function () {
        const xVal = typeof this.x === 'number' ? this.x : 0;
        const yVal = typeof this.y === 'number' ? this.y : 0;
        return `<b>${this.series.name}</b><br/>Saturation: ${xVal.toFixed(2)}<br/>Capillary pressure: ${yVal.toFixed(2)} psi`;
      },
    });

    if (this.chartOptions!.xAxis && Array.isArray(this.chartOptions!.xAxis)) {
      (this.chartOptions!.xAxis as any)[0].tickInterval = 0.1;
      (this.chartOptions!.xAxis as any)[0].minorTickInterval = 0.1;
    } else if (this.chartOptions!.xAxis && typeof this.chartOptions!.xAxis === 'object') {
      (this.chartOptions!.xAxis as any).tickInterval = 0.1;
      (this.chartOptions!.xAxis as any).minorTickInterval = 0.1;
    }
  }

  private initializeChartSeries(): void {
    if (!this.chart) return;

    const allSeries = this.buildChartSeries();
    allSeries.forEach((series) => {
      this.chart!.addSeries(series, false);
    });

    this.chart.redraw();
    this.updateChartSeriesVisibility();
  }

  private reinitializeChartSeries(): void {
    if (!this.chart) return;

    while (this.chart.series.length > 0) {
      this.chart.series[0].remove(false);
    }

    const allSeries = this.buildChartSeries();
    allSeries.forEach((series) => {
      this.chart!.addSeries(series, false);
    });

    this.chart.redraw();
    this.updateChartSeriesVisibility();
  }

  private buildChartSeries(): Highcharts.SeriesOptionsType[] {
    if (!this.selectedSimulation || !this.resultForSelectedSimulation) return [];

    const cycleOrder: PcCycle[] = ['d1', 'imb', 'd2'];
    return cycleOrder.map((cycle) =>
      this.createChartSeriesByCycleType(this.resultForSelectedSimulation!, cycle),
    );
  }

  private createChartSeriesByCycleType(
    result: PcSimulationResults,
    cycle: PcCycle,
  ): Highcharts.SeriesOptionsType {
    let data: number[][];
    let name: string;

    switch (cycle) {
      case 'd1':
        name = `Primary drainage (${result.direction.toUpperCase()})`;
        data = this.swd1Value.map((saturation: string, index: number) => {
          return [Number(saturation), Number(this.pcd1Value[index])];
        });
        break;
      case 'imb':
        name = `First imbibition (${result.direction.toUpperCase()})`;
        data = this.swImbValue.map((saturation: string, index: number) => {
          return [Number(saturation), Number(this.pcImbValue[index])];
        });
        break;
      case 'd2':
        name = `Secondary drainage (${result.direction.toUpperCase()})`;
        data = this.swd2Value.map((saturation: string, index: number) => {
          return [Number(saturation), Number(this.pcd2Value[index])];
        });
        break;
    }

    return createLineSeries({
      name,
      data,
      color: this.defineColorForCycleType(cycle),
      markerEnabled: true,
      custom: {
        direction: result.direction.toLowerCase(),
        cycle,
      },
    });
  }

  private defineColorForCycleType(cycle: PcCycle): string {
    switch (cycle) {
      case 'd1':
        return this.primaryDrainageColor;
      case 'imb':
        return this.firstImbibitionColor;
      case 'd2':
        return this.secondaryDrainageColor;
    }
  }

  private updateChartSeriesVisibility(): void {
    if (!this.chart || !this.resultForSelectedSimulation) return;

    const selectedSeriesNames = new Set<string>();
    const cycleOrder: PcCycle[] = ['d1', 'imb', 'd2'];
    cycleOrder.forEach((cycle) => {
      if (this.getCycleControl(cycle).value) {
        const series = this.createChartSeriesByCycleType(this.resultForSelectedSimulation!, cycle);
        selectedSeriesNames.add(series.name as string);
      }
    });

    updateChartSeriesVisibilitySmooth(this.chart, selectedSeriesNames);
  }

  private mapSortColumn(column: string): string {
    const map: Record<string, string> = {
      name: 'Name',
      fileName: 'File',
      direction: 'Direction',
      cycle: 'Cycle',
      interfactialTension: 'InterfacialTension',
      contactAnglePrimaryDrainage: 'PrimaryDrainage',
      contactAngleFirstImbibition: 'FirstImbibition',
    };
    return map[column] || column;
  }
}
